import { bindActionCreators } from 'redux';
import { connect, Dispatch } from 'react-redux';
import cx from 'classnames';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { OrderedMap } from 'immutable';
import * as React from 'react';
import { keys, isEmpty } from 'lodash';
import invariant from 'invariant';
import { compose } from 'react-apollo';

import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Chat from 'entities/Chat';
import ChatActions from 'chat/actions/ChatActions';
// @ts-ignore
import Composer from 'chat/components/Composer';
// @ts-ignore
import ComposerFilePreview from 'chat/components/ComposerFilePreview';
import ConfirmDialog from 'components/ConfirmDialog';
// @ts-ignore
import ConversationViewComposer from 'chat/components/ConversationViewComposer';
import DateConstants from 'constants/DateConstants';
import DropZoneModal from 'components/DropZoneModal';
import FileEntity from 'entities/File';
import Glyph from 'components/Glyph';
import Image from 'components/Image';
import KeyConstants from 'constants/KeyConstants';
// @ts-ignore
import LanguageHoverCard from 'chat/components/LanguageHoverCard';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
// @ts-ignore
import MessageComposerActions from 'chat/actions/MessageComposerActions';
import MessageComposerStore from 'chat/stores/MessageComposerStore';
import SettingsStore from 'stores/SettingsStore';
// @ts-ignore
import Timing from 'lib/Timing';
import TranslationActions from 'actions/TranslationActions';
import ReduxUploadButton from 'components/ReduxUploadButton';
import { mountModal } from 'lib/modal';
// @ts-ignore
import ComposerAlert from './ComposerAlert';
// @ts-ignore
import withWidth from 'lib/withWidth';
import dateTimeFormat from 'lib/dateTimeFormat';
import {
  getFilesArrayFromFileUploads,
  getFirstFileId,
  getFirstFile,
  uploadFile,
} from 'lib/fileUploadHelper';
import {
  fetchFileById,
  loadThumbnail,
  LoadThumbnailAction,
  removeFile,
  RemoveFileAction,
  resetFile,
  ResetFileAction,
  uploadRequest,
  UploadRequestData,
  UploadRequestGroup,
  State as FileUploadsState,
  UploadRequestFailureHandler,
  UploadRequestSuccessHandler,
} from 'reducers/fileUploads';
import errorLogger from 'utils/errorLogger';

import { Backbone$Id } from 'types/typescript/Backbone';

import './styles/MessageComposer.scss';

const OFFICE_HOURS_NOTICE_TIMEOUT = 60 * 60 * 1000;
const AFTER_HOURS_NOTICE_TIMEOUT = 60 * 60 * 1000;
const CHAR_LIMIT = 1500;
const UI_CONTEXT = 'chat_composer';

type Props = {
  isNewChat?: boolean;
  chat: Chat;
  onMessageSend: (text: string, files: FileEntity[]) => void;
  width: number;
  openSideBar?: () => void;
  className?: string;

  /* dnd props */
  connectDropTarget: (el: React.ReactElement) => React.ReactElement;
  isDraggingFile?: boolean;
};

type ReduxDispatchProps = {
  fetchFileById: (
    fileContextKey: string,
    fileId: React.ReactText
  ) => (dispatch: () => {}) => Promise<void>;
  loadThumbnail: (
    fileContextKey: string,
    fileKey: React.ReactText,
    thumbnail: string
  ) => LoadThumbnailAction;
  removeFile: (fileContextKey: string, fileKey: Backbone$Id) => RemoveFileAction;
  resetFile: (fileContextKey: string, fileKey: Backbone$Id) => ResetFileAction;
  uploadRequest: (
    fileContextKey: string,
    fileKey: React.ReactText,
    blob: File,
    onSuccess?: UploadRequestSuccessHandler,
    onFailure?: UploadRequestFailureHandler,
    privateUpload?: boolean
  ) => (dispatch: () => {}) => Promise<void>;
};

type ReduxStateProps = {
  fileUploads: FileUploadsState;
};

type ReduxIncomingProps = { fileUploads: UploadRequestGroup };

type State = {
  composerError: boolean;
  latestTranslation: string;
  text: string;
  translationBoxOpened: boolean;
};

type monitorType = {
  getItem: () => { files: Array<File> };
  isOver: () => void;
};

const initialState: State = {
  composerError: false,
  latestTranslation: '',
  text: '',
  translationBoxOpened: false,
};

class MessageComposer extends React.Component<
  Props & ReduxDispatchProps & ReduxIncomingProps,
  State
> {
  static MEDIUM = 750;
  static SMALL = 650;
  static XSMALL = 550;
  static XXSMALL = 450;

  state = initialState;

  componentWillMount() {
    if (this.props.chat.id) {
      const newState = MessageComposerStore.getComposerStates(this.props.chat.id);
      if (newState) {
        this.setState(newState);
      }
    }
  }

  componentWillUpdate(nextProps: Props) {
    const currentChatId = this.props.chat.id;
    const nextChatId = nextProps.chat.id;
    const chatChanged = nextChatId !== currentChatId;

    if (chatChanged) {
      // When switching chats, we save the composer state for persisted chats to be restored if we come back to this chat.
      if (!this.props.chat.isNew()) {
        const stateBeforeTimeout = this.state;
        // Timeout to place action at end of dispatch stack
        setTimeout(() => {
          MessageComposerActions.saveState(currentChatId, stateBeforeTimeout);
        }, 0);
      }

      this.removeAllFiles();
      if (this.refs.composer) {
        (this.refs.composer as ConversationViewComposer).clear();
      }

      if (nextChatId) {
        const state = MessageComposerStore.getComposerStates(nextChatId) || initialState;
        this.setState({ ...state });
      }
    }
  }

  componentWillUnmount() {
    if (!this.props.chat.isNew()) {
      setTimeout(() => {
        MessageComposerActions.saveState(this.props.chat.id, this.state);
      }, 0);
    }
    this.removeAllFiles();
  }

  removeAllFiles = () => {
    const { chat, fileUploads } = this.props;
    fileUploads.keySeq().forEach(filename => filename && this.props.removeFile(chat.id, filename));
  };

  getDropzoneId = () => {
    if (this.props.isNewChat) {
      return 'newChat';
    } else {
      return this.props.chat.id;
    }
  };

  getTranslationBox = () => {
    if (!this.state.translationBoxOpened) {
      return null;
    }

    const languages = SettingsStore.getLanguages().primary;

    if (!languages) {
      return null;
    }

    const buttons = [];

    let displayedMax = keys(languages).length;
    let displayed = 0;

    if (this.props.width < MessageComposer.XXSMALL) {
      displayedMax -= 5;
    } else if (this.props.width < MessageComposer.XSMALL) {
      displayedMax -= 3;
    } else if (this.props.width < MessageComposer.SMALL) {
      displayedMax -= 2;
    } else if (this.props.width < MessageComposer.MEDIUM) {
      displayedMax -= 1;
    }

    for (const lang in languages) {
      displayed += 1;
      if (displayed <= displayedMax) {
        buttons.push(
          <Button
            key={`translate-${lang}`}
            eventContextName={UI_CONTEXT}
            eventElementName="translate_button"
            eventProperties={{
              language_code: lang,
            }}
            onClick={() => this.translateTo(lang)}>
            {languages[lang]}
          </Button>
        );
      }
    }

    buttons.push(
      <LayerTrigger
        key="show-more"
        layer={
          <LanguageHoverCard
            eventContextName={UI_CONTEXT}
            onLanguagePicked={(lang: string) => this.translateTo(lang)}
          />
        }
        placement="top"
        trigger="click"
        alignment="middle">
        <Button
          eventContextName={UI_CONTEXT}
          eventElementName="show_more_languages_button"
          level="secondary"
          color="thunder"
          size="xsmall"
          aria-label="More languages">
          <Glyph name="ellipses" solo />
        </Button>
      </LayerTrigger>
    );

    return (
      <div className="MessageComposer-translationBox">
        <div className="MessageComposer-translationBox-buttons">
          <Buttons color="thunder" group level="secondary" size="xsmall">
            {buttons}
          </Buttons>
        </div>

        <Button
          className="MessageComposer-translationBox-closeButton"
          eventContextName={UI_CONTEXT}
          eventElementName="close_translation_box"
          onClick={this.toggleTranslationBoxOpened}
          level="text"
          aria-label="Close">
          <Glyph name="close-circle" color="brand" size="xlarge" solo />
        </Button>
      </div>
    );
  };

  toggleTranslationBoxOpened = () => {
    this.setState(({ translationBoxOpened }) => ({ translationBoxOpened: !translationBoxOpened }));
  };

  allowSendAfterHours: number | null = null;

  allowSendOutsideOfficeHours: { [key: string]: number } = {};

  chatState = {};

  handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;

    if (text === this.state.text) {
      return;
    }

    this.setState({
      text,
      composerError: text.trim().length > CHAR_LIMIT,
    });
  };

  confirmSendMessageAfterHours = () => {
    const timeWarning = Timing.isEarly() ? 'It’s pretty early.' : 'It’s pretty late.';
    const question = 'Send this message anyway?';
    const title = (
      <div>
        <Image
          className="MessageComposer-confirmImage"
          src={require('assets/images/after-hours.svg')}
        />
        <p>
          {timeWarning} {question}
        </p>
      </div>
    );
    mountModal(
      <ConfirmDialog
        title={title}
        eventContext="ChatMessageComposer"
        size="xsmall"
        onConfirm={() => {
          this.allowSendAfterHours = window.setTimeout(() => {
            this.allowSendAfterHours = null;
          }, AFTER_HOURS_NOTICE_TIMEOUT);
          this.sendMessage();
        }}
      />,
      this
    );
  };

  getNextAvailability = () => {
    const { chat } = this.props;
    const otherChatMembers = chat.getOtherChatMembers();

    for (let i = 0; i < otherChatMembers.length; i++) {
      const member = otherChatMembers[i];
      const officeHours = member.getOfficeHours();
      if (!officeHours.isAvailableNow()) {
        return {
          otherChatMember: member,
          nextAvailability: officeHours.getNextAvailability(),
        };
      }
    }
    return {};
  };

  confirmSendMessageOutsideOfOfficeHours = () => {
    const { chat } = this.props;
    const { otherChatMember, nextAvailability } = this.getNextAvailability();

    if (isEmpty(nextAvailability)) {
      throw new Error('unable to determine nextAvailability');
    }

    const startTime = nextAvailability.getStartTime();
    const date = new Date();
    const diff = (nextAvailability.getDay() + 7 - date.getDay()) % 7;
    date.setHours(Math.floor(startTime / 3600));
    date.setMinutes((startTime % 3600) / 60);
    const day = diff === 1 ? 'tomorrow' : DateConstants.WEEKDAYS[nextAvailability.getDay()];
    const time = dateTimeFormat(date, {
      hour: 'numeric',
      minute: '2-digit',
    });
    const otherChatMemberName = otherChatMember?.getName() ?? '???';
    mountModal(
      <ConfirmDialog
        size="xsmall"
        glyph="clock-bubble"
        glyphColor="violet"
        title={`Uh-oh! ${otherChatMemberName} won’t be back until ${day} at ${time}.`}
        subtext={`Office hours for ${otherChatMemberName} won’t start until ${day} at ${time}, so they may not receive your message right now. Send anyway?`}
        onConfirm={() => {
          this.allowSendOutsideOfficeHours[chat.id] = window.setTimeout(() => {
            delete this.allowSendOutsideOfficeHours[chat.id];
          }, OFFICE_HOURS_NOTICE_TIMEOUT);
          this.sendMessage();
        }}
      />,
      this
    );
  };

  focus = () => {
    if (this.refs.composer) {
      (this.refs.composer as ConversationViewComposer).focus();
    }
  };

  handleEnableRepliesButtonClick = (event: Event) => {
    event.preventDefault();
    ChatActions.enableReplies(this.props.chat);
  };

  isChatEnabled = () => {
    return this.props.chat.isPresent() && !this.props.chat.isUnsubscribed();
  };

  isValidMessage = () => {
    const { text } = this.state;
    const { fileUploads } = this.props;

    const allUploadsComplete = fileUploads.every((file: UploadRequestData | undefined) => {
      return file?.progressPercentage === 100;
    });
    const hasText = text.trim().length > 0;

    return allUploadsComplete && (hasText || !fileUploads.isEmpty());
  };

  getPlaceholder = () => {
    const chatTitle = this.props.chat.getTitle();
    if (chatTitle) {
      return `Message ${chatTitle}`;
    } else {
      return 'Type a message';
    }
  };

  sendMessage = () => {
    const { chat, fileUploads } = this.props;
    if (chat.isOutsideOfOtherOfficeHours() && !this.allowSendOutsideOfficeHours[chat.id]) {
      this.confirmSendMessageOutsideOfOfficeHours();
    } else if ((Timing.isLate() || Timing.isEarly()) && !this.allowSendAfterHours) {
      this.confirmSendMessageAfterHours();
    } else {
      this.props.onMessageSend(this.state.text, getFilesArrayFromFileUploads(fileUploads));
      (this.refs.composer as ConversationViewComposer).clear();
      this.setState({
        text: '',
      });
      this.removeAllFiles();
    }
  };

  handleReturn = (event: React.KeyboardEvent<HTMLInputElement>): boolean => {
    if (event.keyCode === KeyConstants.ENTER && !event.shiftKey) {
      this.handleSubmit(event);
      return true;
    }
    return false;
  };

  handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    this.submitComposer();
  };

  submitComposer = () => {
    if (!this.submitDisabled()) {
      this.sendMessage();
    }
  };

  submitDisabled = () => {
    return (
      this.state.translationBoxOpened ||
      this.state.composerError ||
      !this.isChatEnabled() ||
      !this.isValidMessage()
    );
  };

  translateTo = (lang: string) => {
    TranslationActions.translate(this.state.text, lang)
      .then(response => {
        this.setState({
          text: response.translation,
          latestTranslation: lang,
          translationBoxOpened: false,
        });
      })
      .catch(errorLogger('TranslationActions.translate failed'));
  };

  retryUpload = (filename: Backbone$Id) => {
    const { chat, fileUploads } = this.props;
    this.props.resetFile(chat.id, filename);
    const file = fileUploads.get(filename);
    if (!file) {
      throw 'MessageComposer retryUpload missing file';
    }
    this.props.uploadRequest(chat.id, filename, file.blob);
  };

  renderMessageComposer = () => {
    const { composerError, translationBoxOpened, latestTranslation } = this.state;
    const { chat, fileUploads, isDraggingFile } = this.props;

    if (!chat.canSend() || chat.isInsufficientlyConnected()) {
      return null;
    }
    let errorMessage = null;
    if (composerError) {
      errorMessage = (
        <div className="MessageComposer-error">
          Sorry, you can’t send more than {CHAR_LIMIT} characters
        </div>
      );
    }
    const attachButton = (
      <ReduxUploadButton className="MessageComposer-attach" level="text" fileContextKey={chat.id}>
        <Glyph name="paperclip" className="MessageComposer-actionGlyph" />
        Attach
      </ReduxUploadButton>
    );
    const translateButton = (
      <Button
        className="MessageComposer-translate"
        disabled={this.state.text.length < 1}
        eventContextName={UI_CONTEXT}
        eventElementName={translationBoxOpened ? 'hide_translation_box' : 'show_translation_box'}
        onClick={() =>
          this.setState({
            translationBoxOpened: !translationBoxOpened,
          })
        }
        level="text">
        <Glyph name="globe" className="MessageComposer-actionGlyph" />
        <span>Translate</span>
      </Button>
    );

    const firstFileId = getFirstFileId(fileUploads);
    const uploadRequestData = getFirstFile(fileUploads);
    const removeFirstFile = () => firstFileId && this.props.removeFile(chat.id, firstFileId);
    const retryFirstFile = () => firstFileId && this.retryUpload(firstFileId);

    const filePreview = uploadRequestData ? (
      <div className="MessageComposer-preview">
        <ComposerFilePreview
          className={cx({
            draggingFile: isDraggingFile,
          })}
          progress={uploadRequestData.progressPercentage}
          thumbnail={uploadRequestData.thumbnail}
          type={uploadRequestData.file && uploadRequestData.file.getContentType()}
          error={uploadRequestData.isError}
          onDelete={removeFirstFile}
          onRetry={retryFirstFile}
          file={uploadRequestData.file}
        />
      </div>
    ) : null;

    const eventProperties: { language_code?: string } = {};
    if (latestTranslation) {
      eventProperties.language_code = latestTranslation;
    }
    const otherMembersPlaceholderText = this.getPlaceholder();
    return (
      <div className="MessageComposer-composerContainer" data-test-selector="message-composer">
        <MediaObject figure="right" gutter="xsmall">
          <div>
            <Composer
              autogrow
              autoFocus
              constrainMaxHeight
              ref="composer"
              className="MessageComposer-composer"
              onChange={this.handleTextChange}
              onReturnKeyPress={this.handleReturn}
              error={composerError}
              value={this.state.text}
              placeholder={otherMembersPlaceholderText}
              disabled={!this.isChatEnabled()}>
              {filePreview}
              {this.getTranslationBox()}
            </Composer>
            {errorMessage}
            <div className="MessageComposer-actions">
              {attachButton}
              {translateButton}
            </div>
          </div>
          <Button
            className="MessageComposer-sendButton"
            data-test-selector="message-composer-send-button"
            eventContextName={UI_CONTEXT}
            eventElementName="send_button"
            eventProperties={eventProperties}
            level="primary"
            size="small"
            onClick={this.handleSubmit}
            disabled={this.submitDisabled()}>
            Send
          </Button>
        </MediaObject>
      </div>
    );
  };

  render() {
    const {
      chat,
      className,
      connectDropTarget,
      fileUploads,
      isDraggingFile,
      openSideBar,
    } = this.props;

    const dropzone = (
      <div>
        <DropZoneModal
          titleDrop="Drop a file here"
          isFileUploaded={!fileUploads.isEmpty()}
          isHovering
        />
      </div>
    );
    const composerAlert = <ComposerAlert chat={chat} openSideBar={openSideBar} />;
    return connectDropTarget(
      <div className={cx('MessageComposer', className)}>
        {isDraggingFile && dropzone}
        {composerAlert}
        {this.renderMessageComposer()}
      </div>
    );
  }
}

const dndEvents = {
  drop: (props: Props & ReduxStateProps & ReduxDispatchProps, monitor: monitorType) => {
    const file = monitor.getItem().files[0];
    uploadFile(props.chat.id, file, props.uploadRequest, props.loadThumbnail);
  },
  canDrop: (props: ReduxStateProps) => {
    return props.fileUploads.isEmpty(); // can only attach one file at a time
  },
};

const collect = (connector: { dropTarget: () => void }, monitor: monitorType) => ({
  connectDropTarget: connector.dropTarget(),
  isDraggingFile: monitor.isOver(),
});

const mapStateToProps = (state: ReduxStateProps, props: Props): ReduxIncomingProps => {
  const chatId = props?.chat?.id;
  const fileGroup = state?.fileUploads?.get(chatId);
  return {
    fileUploads: fileGroup ?? OrderedMap<Backbone$Id, UploadRequestData>(),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>, ownProps: Props): ReduxDispatchProps =>
  bindActionCreators(
    {
      fetchFileById,
      removeFile,
      resetFile,
      uploadRequest,
      loadThumbnail,
    },
    dispatch
  );
const type = NativeTypes.FILE;

export { MessageComposer };

export const ReduxMessageComposer = connect(mapStateToProps, mapDispatchToProps)(MessageComposer);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withWidth,
  DropTarget(type, dndEvents, collect)
)(MessageComposer);

export const CHARACTER_LIMIT = CHAR_LIMIT;
